import type { TFunction } from 'i18next';
import { notificationController } from '@app/controllers/notificationController';
import {
  isI18nMessageWithParams,
  resolveTranslation,
  translateFromApiErrorPayload,
  type ApiErrorPayload,
} from '@app/utils/translateI18nMessage';
import { OPAQUE_ERROR_CODES, type NormalizedApiError } from '@app/api/errorNormalizer';

export function notifyApiError(t: TFunction, payload: ApiErrorPayload): void {
  const { errorCode, errorDetails } = payload;
  const description = translateFromApiErrorPayload(t, payload);

  let message: string;
  if (errorCode?.trim()) {
    message = errorCode.trim();
  } else if (isI18nMessageWithParams(errorDetails)) {
    message = errorDetails[0];
  } else {
    const fallback = (t('common.error') as string)?.trim();
    message = fallback || 'Error';
  }

  notificationController.error({ message, description });
}

/**
 * A burst of failures (backend down, network dropped) would otherwise raise one toast
 * per request: each carries its own reference id, so `notificationController` cannot
 * collapse them. Same window as the controller's dedupe; the first toast wins.
 */
const GENERIC_TOAST_WINDOW_MS = 2000;
const lastGenericToastAt = new Map<string, number>();

/**
 * Generic strings are whole sentences, so they go in the message slot. Without a
 * reference id the description-less notification renders in `title-only` style.
 */
function notifyGeneric(t: TFunction, key: string, referenceId?: string): void {
  const now = Date.now();
  const lastShownAt = lastGenericToastAt.get(key);
  if (lastShownAt !== undefined && now - lastShownAt < GENERIC_TOAST_WINDOW_MS) {
    return;
  }
  lastGenericToastAt.set(key, now);

  const message = resolveTranslation(t, key) ?? resolveTranslation(t, 'common.somethingWentWrong') ?? '';
  notificationController.error({
    message,
    description: referenceId ? formatReferenceLabel(t, referenceId) : undefined,
    copyText: referenceId,
  });
}

export function formatReferenceLabel(t: TFunction, referenceId: string): string {
  const translated = (t('errors.referenceId', { id: referenceId }) as string)?.trim();
  return translated && translated !== 'errors.referenceId' ? translated : `Reference: ${referenceId}`;
}

/**
 * Toast for a {@link NormalizedApiError} from the axios interceptor. Transport
 * failures and backend crashes get our own translated wording, since theirs is
 * absent or untranslated; everything else keeps the server's specific reason.
 */
export function notifyNormalizedApiError(t: TFunction, normalized: NormalizedApiError, referenceId?: string): void {
  if (normalized.isNetworkError) {
    notifyGeneric(t, 'errors.networkError', referenceId);
    return;
  }

  if (normalized.isTimeout) {
    notifyGeneric(t, 'errors.timeout', referenceId);
    return;
  }

  const isServerCrash =
    (normalized.errorCode != null && OPAQUE_ERROR_CODES.has(normalized.errorCode)) ||
    (normalized.status != null && normalized.status >= 500);

  if (isServerCrash) {
    notifyGeneric(t, 'errors.internalServerError', referenceId);
    return;
  }

  notifyApiError(t, {
    errorCode: normalized.errorCode,
    errorMessage: normalized.errorMessage,
    errorDetails: normalized.errorDetails,
  });
}
